import moment, { type Moment } from "moment-timezone"

import {
    AvailablePlanType,
    BlueCardEligibility,
    CoverageCheckResponseType,
    ResetBenefitStatus,
} from "@common-shared/config/coverage.config"
import {
    AvailableCoverageHistorySortDirection,
    AvailableCoverageHistorySortField,
} from "@common-shared/config/coverageHistory.config"
import { type FeatureFlag } from "@common-shared/config/featureFlags.config"
import {
    AgenticCheckStatus,
    CoveragePatientType,
    OneVerifyFlagType,
} from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import {
    PoliciesListType,
    PolicyAssignability,
    PolicyAssignmentAppointmentType,
    PolicyAssignmentStatus,
    PolicyAssignmentWorkflowStatus,
    PolicyCredentialingStatus,
    PolicyModality,
    PolicyModalityCode,
    PolicyRangeDateFilterType,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import type { IPagination } from "@common-shared/types/general.types"
import {
    ICoverageHistoryAggregationsStatusCodes,
    ICoverageHistoryListSort,
} from "@common-shared/types/internalApis/coverageHistory.types"
import {
    IPoliciesDateRangeFilterData,
    IPoliciesListFiltersData,
    IPolicyColumnConfiguration,
} from "@common-shared/types/policies.types"
import {
    IOneVerifyCoverageCheckHistory,
    IOneVerifyOrganizationUser,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import type { ISelectRawOption } from "@front-shared/types/objects.types"

import { CHECKER_CONFIG } from "../config/checker.config"
import { COVERAGE_CONFIG } from "../config/coverage.config"
import { GENERAL_CONFIG } from "../config/general.config"
import {
    POLICIES_CONFIG,
    PolicyHeaderDragDirection,
} from "../config/policies.config"
import { ROUTES_CONFIG } from "../config/routes.config"
import { TOOLTIPS_CONFIG } from "../config/tooltips.config"
import type {
    IPoliciesAggregatedChartValues,
    IReportsStatusCodesWidgetConfig,
} from "../types/objects.types"
import type { IPoliciesState } from "../types/state.types"
import { ExportReportHelper } from "./exportReport.helper"
import { FeatureFlagsHelper } from "./featureFlags.helper"
import { UtilHelper } from "./util.helper"

export class PoliciesListHelper {
    /**
     * Get policies list base url based on general view type
     */
    static getPoliciesListBaseUrl(viewType: PoliciesListType): string {
        switch (viewType) {
            case PoliciesListType.REPORTS:
                return ROUTES_CONFIG.reportsUrl

            case PoliciesListType.HISTORY_SEARCH:
                return ROUTES_CONFIG.searchUrl

            case PoliciesListType.OVERRIDES:
                return ROUTES_CONFIG.overridesUrl

            case PoliciesListType.PLAN_YEAR_RESETS:
                return ROUTES_CONFIG.planYearResetsUrl

            case PoliciesListType.MEDICAID:
                return ROUTES_CONFIG.medicaidReportsUrl

            case PoliciesListType.AGENTIC:
                return ROUTES_CONFIG.agenticReportsUrl

            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                return ROUTES_CONFIG.assignedTasksUrl

            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                return ROUTES_CONFIG.taskAssignmentsUrl
        }
    }

    /**
     * Get data from state that depends on view type
     */
    static getViewTypedStateData(
        viewType: PoliciesListType,
        policiesState: IPoliciesState,
    ): {
        policyItems: IOneVerifyCoverageCheckHistory[]
        nextPagination: IPagination
        sortConfiguration: ICoverageHistoryListSort
        filtersConfiguration: IPoliciesListFiltersData
        isGettingListError: boolean
    } {
        switch (viewType) {
            case PoliciesListType.REPORTS:
                return {
                    policyItems: policiesState.reportsItems,
                    nextPagination: policiesState.reportsPagination,
                    sortConfiguration: policiesState.reportsSort,
                    filtersConfiguration: policiesState.reportsFilters,
                    isGettingListError: policiesState.reportsIsError,
                }

            case PoliciesListType.HISTORY_SEARCH:
                return {
                    policyItems: policiesState.historyItems,
                    nextPagination: policiesState.historyPagination,
                    sortConfiguration: policiesState.historySort,
                    filtersConfiguration: policiesState.historyFilters,
                    isGettingListError: policiesState.historyIsError,
                }

            case PoliciesListType.OVERRIDES:
                return {
                    policyItems: policiesState.overridesItems,
                    nextPagination: policiesState.overridesPagination,
                    sortConfiguration: policiesState.overridesSort,
                    filtersConfiguration: policiesState.overridesFilters,
                    isGettingListError: policiesState.overridesIsError,
                }

            case PoliciesListType.PLAN_YEAR_RESETS:
                return {
                    policyItems: policiesState.planYearResetsItems,
                    nextPagination: policiesState.planYearResetsPagination,
                    sortConfiguration: policiesState.planYearResetsSort,
                    filtersConfiguration: policiesState.planYearResetsFilters,
                    isGettingListError: policiesState.planYearResetsIsError,
                }

            case PoliciesListType.MEDICAID:
                return {
                    policyItems: policiesState.medicaidItems,
                    nextPagination: policiesState.medicaidPagination,
                    sortConfiguration: policiesState.medicaidSort,
                    filtersConfiguration: policiesState.medicaidFilters,
                    isGettingListError: policiesState.medicaidIsError,
                }

            case PoliciesListType.AGENTIC:
                return {
                    policyItems: policiesState.agenticItems,
                    nextPagination: policiesState.agenticPagination,
                    sortConfiguration: policiesState.agenticSort,
                    filtersConfiguration: policiesState.agenticFilters,
                    isGettingListError: policiesState.agenticIsError,
                }

            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                return {
                    policyItems: policiesState.assignedTasksItems,
                    nextPagination: policiesState.assignedTasksPagination,
                    sortConfiguration: policiesState.assignedTasksSort,
                    filtersConfiguration: policiesState.assignedTasksFilters,
                    isGettingListError: policiesState.assignedTasksIsError,
                }

            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                return {
                    policyItems: policiesState.taskAssignmentsItems,
                    nextPagination: policiesState.taskAssignmentsPagination,
                    sortConfiguration: policiesState.taskAssignmentsSort,
                    filtersConfiguration: policiesState.taskAssignmentsFilters,
                    isGettingListError: policiesState.taskAssignmentsIsError,
                }
        }
    }

    /**
     * Get columns based on view type, available modalities, whether it's an export, and whether to use saved configuration
     * Uses LOCAL storage
     */
    static getColumnsConfiguration(
        viewType: PoliciesListType,
        availableModalities = [PolicyModality.MENTAL_HEALTH],
        isExport = false,
        useSavedConfiguration = true,
    ): IPolicyColumnConfiguration[] {
        const resolvedFeatureFlags = BrowserStorageHelper.get<FeatureFlag[]>(
            GENERAL_CONFIG.browserStorageKeys.enabledFeatureFlags,
            undefined,
            BrowserStorageType.sessionStorage,
        )

        let columns = ExportReportHelper.getDefaultColumns(
            viewType,
            availableModalities,
        )

        if (useSavedConfiguration) {
            columns = PoliciesListHelper.getDefaultPoliciesColumnsConfiguration(
                PoliciesListHelper.getModalitiesBasedStorageKey(
                    viewType +
                        GENERAL_CONFIG.browserStorageKeys
                            .policiesColumnsConfigurationSuffix,
                    availableModalities,
                ),
                columns,
            )
        }

        const baseColumns = columns.filter(item =>
            isExport ? !item.isExportIgnored : !item.isExportSpecific,
        )

        return baseColumns.filter(item => {
            return (
                !item.featureFlags?.length ||
                FeatureFlagsHelper.isFeatureFlagEnabled(
                    resolvedFeatureFlags || [],
                    item.featureFlags,
                    item.featureFlagsExistenceOperator,
                )
            )
        })
    }

    /**
     * Get default configuration for sort for different views
     * Uses LOCAL storage
     */
    static getSavedSortConfiguration(
        viewType: PoliciesListType,
    ): ICoverageHistoryListSort {
        const savedConfiguration =
            BrowserStorageHelper.get<ICoverageHistoryListSort>(
                viewType +
                    GENERAL_CONFIG.browserStorageKeys
                        .policiesSortConfigurationSuffix,
            )

        if (
            !savedConfiguration?.sortBy ||
            !Object.values(AvailableCoverageHistorySortField).includes(
                savedConfiguration.sortBy,
            )
        ) {
            return PoliciesListHelper.getDefaultSortConfiguration(viewType)
        }

        return savedConfiguration
    }

    /**
     * Get default filters configuration for different views
     */
    private static getDefaultSortConfiguration(
        viewType: PoliciesListType,
    ): ICoverageHistoryListSort {
        switch (viewType) {
            case PoliciesListType.HISTORY_SEARCH:
            case PoliciesListType.REPORTS:
            case PoliciesListType.PLAN_YEAR_RESETS:
            case PoliciesListType.MEDICAID:
            case PoliciesListType.AGENTIC:
            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                return {
                    sortBy: AvailableCoverageHistorySortField.timestamp,
                    sortDirection: AvailableCoverageHistorySortDirection.DESC,
                }

            case PoliciesListType.OVERRIDES:
                return {
                    sortBy: AvailableCoverageHistorySortField.overrideExpirationDate,
                    sortDirection: AvailableCoverageHistorySortDirection.ASC,
                }

            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                return {
                    sortBy: AvailableCoverageHistorySortField.assignmentCreatedAt,
                    sortDirection: AvailableCoverageHistorySortDirection.DESC,
                }
        }
    }

    /**
     * Get default configuration for filters for different views
     * Uses LOCAL storage
     * If no filters are found in local storage and returnDefaultOnEmpty is true, return default filters configuration
     */
    static getSavedFiltersConfiguration(
        viewType: PoliciesListType,
        returnDefaultOnEmpty = true,
    ): IPoliciesListFiltersData {
        return (
            BrowserStorageHelper.get(
                viewType +
                    GENERAL_CONFIG.browserStorageKeys
                        .policiesFiltersConfigurationSuffix,
            ) || {
                ...(returnDefaultOnEmpty
                    ? PoliciesListHelper.getDefaultFiltersConfiguration(
                          viewType,
                      )
                    : {}),
            }
        )
    }

    /**
     * Get default filters configuration for different views
     */
    static getDefaultFiltersConfiguration(
        viewType: PoliciesListType,
    ): IPoliciesListFiltersData {
        switch (viewType) {
            case PoliciesListType.OVERRIDES:
            case PoliciesListType.MEDICAID:
            case PoliciesListType.AGENTIC:
            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                return {
                    checkDate: {
                        type: PolicyRangeDateFilterType.LAST_X_DAYS,
                        daysAmount: POLICIES_CONFIG.defaultReportsGoBackDays,
                    },
                }

            case PoliciesListType.REPORTS:
                return {
                    checkDate: {
                        type: PolicyRangeDateFilterType.LAST_X_DAYS,
                        daysAmount: POLICIES_CONFIG.defaultReportsGoBackDays,
                    },
                    responseType:
                        POLICIES_CONFIG.defaultResponseTypesByListType[
                            PoliciesListType.REPORTS
                        ],
                }

            case PoliciesListType.HISTORY_SEARCH:
                return {
                    checkDate: {
                        type: PolicyRangeDateFilterType.LAST_X_DAYS,
                        daysAmount: POLICIES_CONFIG.defaultSearchGoBackDays,
                    },
                    responseType:
                        POLICIES_CONFIG.defaultResponseTypesByListType[
                            PoliciesListType.HISTORY_SEARCH
                        ],
                }

            case PoliciesListType.PLAN_YEAR_RESETS:
                return {
                    checkDate: {
                        type: PolicyRangeDateFilterType.LAST_X_DAYS,
                        daysAmount:
                            POLICIES_CONFIG.defaultReportsExtendedGoBackDays,
                    },
                }
        }
    }

    /**
     * Save columns configurations for future usage
     */
    static saveColumnsConfigurations(
        viewType: PoliciesListType,
        columnsConfigurations: IPolicyColumnConfiguration[],
        availableModalities = [PolicyModality.MENTAL_HEALTH],
    ) {
        const key =
            viewType +
            GENERAL_CONFIG.browserStorageKeys.policiesColumnsConfigurationSuffix

        BrowserStorageHelper.set(
            PoliciesListHelper.getModalitiesBasedStorageKey(
                key,
                availableModalities,
            ),
            columnsConfigurations,
            BrowserStorageType.localStorage,
        )
    }

    /**
     * Move column to some other place in list
     */
    static changeSingleColumnOrdering(
        columns: IPolicyColumnConfiguration[],
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ): IPolicyColumnConfiguration[] {
        // First find and remove needed column
        const source = columns.find(
            item => item.label === columnLabelMoveSource,
        )

        if (!source) {
            return columns
        }

        const newColumns = columns.filter(item => item.label !== source.label)

        // Find target column index
        const targetIndex = newColumns.findIndex(
            item => item.label === columnLabelMoveTarget,
        )

        if (targetIndex < 0) {
            return columns
        }

        // Adjust based on drag direction
        // If after add 1
        newColumns.splice(
            targetIndex +
                (direction === PolicyHeaderDragDirection.AFTER ? 1 : 0),
            0,
            source,
        )

        return newColumns
    }

    /**
     * Calculate value for aggregation by status code
     */
    static getStatusCodeAggregationValue(
        data: ICoverageHistoryAggregationsStatusCodes,
        config: IReportsStatusCodesWidgetConfig,
    ): number {
        if (!config.aggregationRules?.length) {
            return 0
        }

        let result = 0

        for (const rule of config.aggregationRules) {
            if (rule.statusCode && !rule.errorCode) {
                result += data.aggregations[rule.statusCode]?.total || 0

                continue
            }

            // Below we are mapping through error code, so need to be set
            if (!rule.errorCode) {
                continue
            }

            // That is set on rule, or all if not set
            const neededStatusCodes = rule.statusCode
                ? [rule.statusCode]
                : Object.keys(data.aggregations)

            for (const statusCode of neededStatusCodes) {
                result +=
                    data.aggregations[statusCode]?.detail?.[rule.errorCode] || 0
            }
        }

        return result
    }

    /**
     * Convert plan year resets status to chart values
     */
    static mapPlanYearResetsStatusToChartValues(
        resetBenefitStatus: ResetBenefitStatus,
        value: number,
        valueComparePercentage: number,
        includeTooltip = false,
    ): IPoliciesAggregatedChartValues {
        return {
            label: COVERAGE_CONFIG.selectRenderedResetBenefitStatus[
                resetBenefitStatus
            ].displayValue,

            color: POLICIES_CONFIG.planYearResetsStatusColors[
                resetBenefitStatus
            ],
            bordered: resetBenefitStatus === ResetBenefitStatus.NOT_DETECTED,

            value,
            percentage: UtilHelper.getPercentageValue(
                value,
                valueComparePercentage,
            ),
            tooltip: includeTooltip
                ? TOOLTIPS_CONFIG.reports.visualizations.resetBenefitsStatus[
                      resetBenefitStatus
                  ]
                : undefined,
        }
    }

    /**
     * Get filters that are mapped for display
     */
    static getMappedDisplayFilters(
        filters: IPoliciesListFiltersData,
        listType: PoliciesListType,
        users?: IOneVerifyOrganizationUser[],
    ): ISelectRawOption[] {
        const appliedFiltersKeys = Object.keys(
            filters,
        ) as (keyof IPoliciesListFiltersData)[]

        return appliedFiltersKeys
            .map(key => {
                // Hide has some filters completely from UI
                if (POLICIES_CONFIG.filtersHiddenDisplayKeys.includes(key)) {
                    return null
                }

                let value: any = filters[key]

                switch (key) {
                    case "checkDate":
                    case "nextAppointmentDate":
                    case "planEndDate":
                    case "overrideExpirationDate":
                    case "taskAssignmentDateWorked":
                    case "customFieldAppointmentCreatedTimestamp":
                        if (!value) {
                            return null
                        }

                        const infiniteDateFallback = "∞"

                        const { from, to } =
                            PoliciesListHelper.getRelativeDateRangeValues(value)

                        value = `${from || infiniteDateFallback} - ${
                            to || infiniteDateFallback
                        }`

                        break

                    case "responseType":
                        const neededResponseTypes = Array.isArray(value)
                            ? value
                            : [value]

                        value = neededResponseTypes
                            .map((responseType: CoverageCheckResponseType) => {
                                return COVERAGE_CONFIG.responseTypeMapping[
                                    responseType
                                ]
                            })
                            .join(", ")

                        break

                    case "memberDob":
                        value = value
                            ? moment(value).format(
                                  GENERAL_CONFIG.defaultMomentDateFormat,
                              )
                            : value

                        break

                    case "payer":
                        value = filters.payer?.insuranceName || null

                        break

                    case "flags":
                        value =
                            value && Array.isArray(value)
                                ? value
                                      .map(
                                          flag =>
                                              POLICIES_CONFIG.flagTypeMappings[
                                                  flag as OneVerifyFlagType
                                              ].label,
                                      )
                                      .join(", ")
                                : value

                        break

                    case "patientType":
                        value = value
                            ? COVERAGE_CONFIG.selectRenderedPatientTypes[
                                  value as CoveragePatientType
                              ]?.displayValue
                            : null

                        break

                    case "resetBenefitsStatus":
                        value = value
                            ? COVERAGE_CONFIG.selectRenderedResetBenefitStatus[
                                  value as ResetBenefitStatus
                              ]?.displayValue
                            : null

                        break

                    case "planType":
                        value = value
                            ? CHECKER_CONFIG.planTypeMapping[
                                  value as AvailablePlanType
                              ]
                            : null

                        break

                    case "modalityCode":
                        value = value
                            ? COVERAGE_CONFIG.modalityCodeDisplayNameMapping[
                                  value as PolicyModalityCode
                              ]
                            : null

                        break

                    case "agenticCheckStatus":
                        value = value
                            ? POLICIES_CONFIG.agenticCheckStatusMapping[
                                  value as AgenticCheckStatus
                              ]
                            : null

                        break

                    case "taskAssignmentAssignability":
                        value = value
                            ? POLICIES_CONFIG.policyAssignabilityMapping[
                                  value as PolicyAssignability
                              ]
                            : null

                        break

                    case "taskAssignmentsUnassigned":
                        value = value ? "Yes" : null

                        break

                    case "bluecardEligibility":
                        value = value
                            ? COVERAGE_CONFIG.bluecardEligibilityMapping[
                                  value as BlueCardEligibility
                              ]
                            : null

                        break

                    case "taskAssignmentAssignee":
                    case "taskAssignmentAssignedBy":
                    case "checkedByUsers":
                        if (value && users?.length && Array.isArray(value)) {
                            // Handle multiple user IDs - map to user names
                            const names = value
                                .map(userId => {
                                    const user = users.find(
                                        u => u.id === userId,
                                    )

                                    return user?.name || userId
                                })
                                .filter(Boolean)

                            value = names.join(", ")
                        }

                        break

                    case "taskAssignmentStatus":
                        if (value && Array.isArray(value)) {
                            // Handle multiple statuses - filter out unmapped statuses
                            const mapping =
                                POLICIES_CONFIG.policyAssignmentStatusMapping(
                                    listType,
                                )
                            const names = value
                                .map(
                                    status =>
                                        mapping[
                                            status as PolicyAssignmentStatus
                                        ],
                                )
                                .filter((name): name is string => !!name)

                            value = names.length ? names.join(", ") : null
                        }

                        break

                    case "customFieldAppointmentType":
                        value = value
                            ? POLICIES_CONFIG
                                  .policyAssignmentAppointmentTypeMapping[
                                  value as PolicyAssignmentAppointmentType
                              ] || value
                            : null

                        break

                    case "customFieldWorkflowStatus":
                        value = value
                            ? POLICIES_CONFIG
                                  .policyAssignmentWorkflowStatusMapping[
                                  value as PolicyAssignmentWorkflowStatus
                              ] || value
                            : null

                        break

                    case "customFieldCredentialingStatus":
                        if (value && Array.isArray(value)) {
                            // Convert enum keys to human-readable values
                            const names = (
                                value as (keyof typeof PolicyCredentialingStatus)[]
                            )
                                .map(
                                    statusKey =>
                                        PolicyCredentialingStatus[statusKey],
                                )
                                .filter(Boolean)

                            value = names.length ? names.join(", ") : null
                        }

                        break
                }

                if (!value) {
                    return null
                }

                // Ensure value is a string to prevent React rendering errors
                const stringValue =
                    typeof value === "string" ? value : JSON.stringify(value)

                return {
                    key,
                    label: POLICIES_CONFIG.filtersDisplayMapping[key],
                    value: stringValue,
                }
            })
            .filter(item => !!item) as ISelectRawOption[]
    }

    /**
     * Get relative date range values
     */
    static getRelativeDateRangeValues(
        relativeDateRange: IPoliciesDateRangeFilterData,
        relativeToDate?: Date | Moment,
    ): { from?: string; to?: string } {
        let from: Moment | undefined
        let to: Moment | undefined

        switch (relativeDateRange.type) {
            case PolicyRangeDateFilterType.TODAY:
                from = moment(relativeToDate).startOf("day")
                to = moment(relativeToDate).endOf("day")

                break

            case PolicyRangeDateFilterType.DAY_BEFORE:
                from = moment(relativeToDate).subtract(1, "day").startOf("day")
                to = moment(relativeToDate).endOf("day")

                break

            case PolicyRangeDateFilterType.DAY_AFTER:
                from = moment(relativeToDate).startOf("day")
                to = moment(relativeToDate).add(1, "day").endOf("day")

                break

            case PolicyRangeDateFilterType.LAST_X_DAYS:
                from = relativeDateRange.daysAmount
                    ? moment(relativeToDate)
                          .subtract(relativeDateRange.daysAmount, "days")
                          .startOf("day")
                    : undefined

                to = moment(relativeToDate).endOf("day")

                break

            case PolicyRangeDateFilterType.NEXT_X_DAYS:
                from = moment(relativeToDate).startOf("day")
                to = relativeDateRange.daysAmount
                    ? moment(relativeToDate)
                          .add(relativeDateRange.daysAmount, "days")
                          .endOf("day")
                    : undefined

                break

            case PolicyRangeDateFilterType.CUSTOM_RANGE:
                from = relativeDateRange.dateFrom
                    ? moment(relativeDateRange.dateFrom).startOf("day")
                    : undefined

                to = relativeDateRange.dateTo
                    ? moment(relativeDateRange.dateTo).endOf("day")
                    : undefined
        }

        return {
            from: from?.format(GENERAL_CONFIG.defaultMomentDateFormat),
            to: to?.format(GENERAL_CONFIG.defaultMomentDateFormat),
        }
    }

    /**
     * Get storage key that is modalities based
     */
    private static getModalitiesBasedStorageKey(
        key: string,
        availableModalities: PolicyModality[],
    ): string {
        return `${key}_${availableModalities.join("_")}`
    }

    /**
     * Get default values for policies columns configurations
     */
    private static getDefaultPoliciesColumnsConfiguration(
        browserStorageKey: string,
        defaultValues: IPolicyColumnConfiguration[],
    ): IPolicyColumnConfiguration[] {
        const browserStorageColumns = BrowserStorageHelper.get<
            IPolicyColumnConfiguration[]
        >(browserStorageKey, undefined, BrowserStorageType.localStorage)

        if (!browserStorageColumns?.length) {
            return defaultValues
        }

        let result = browserStorageColumns

        // Add columns that exist in defaults but don't in existing
        // It will be added to the same place as it's in new config (after same column)
        // If same column can't be found -> it will be added to the end
        const missingColumns = defaultValues.filter(
            item =>
                !result.find(
                    innerItem => innerItem.columnKey === item.columnKey,
                ),
        )

        const notAddedMissingColumns: IPolicyColumnConfiguration[] = []

        for (const missingColumn of missingColumns) {
            const index = defaultValues.findIndex(
                item => item.columnKey === missingColumn.columnKey,
            )

            if (index > -1) {
                result.splice(index, 0, missingColumn)
            } else {
                notAddedMissingColumns.push(missingColumn)
            }
        }

        result = [...result, ...notAddedMissingColumns]

        // Remove columns that are not in new config
        result = result.filter(item =>
            defaultValues.find(
                innerItem => innerItem.columnKey === item.columnKey,
            ),
        )

        // Extend existing columns with values from default
        // Keep just isEnabled value same
        return result.map(item => {
            const defaultItem = defaultValues.find(
                innerItem => item.columnKey === innerItem.columnKey,
            )

            return defaultItem
                ? {
                      ...defaultItem,

                      isEnabled: item.isEnabled,
                  }
                : item
        })
    }
}
